<script setup lang="ts">
import { computed, defineAsyncComponent, ref, watch } from 'vue'

import { VueDraggable } from 'vue-draggable-plus'

import BetaBaseImage from '@/components/beta-editor/BetaBaseImage.vue'
import { useEditorBetaStore } from '@/stores/editor/beta'

const BetaEditorElement = defineAsyncComponent(() => import('@/components/beta-editor/BetaEditorElement.vue'))

const props = defineProps<{
    element: any
    extra: any
    elementIndex: number
    sectionIndex: number
    sectionBackgroundColor: string
    sectionHasBackgroundImage?: boolean
    isDarkMode: boolean
    isFull: boolean
    isNew?: boolean
    isNewBefore?: boolean
    isEditable?: boolean
    schema?: any
    headingStyle?: Record<string, string>
}>()

const betaStore = useEditorBetaStore()
const content = ref<any[]>(Array.isArray(props.element.frameContent) ? props.element.frameContent : [])

watch(
    () => props.element.frameContent,
    value => {
        content.value = Array.isArray(value) ? value : []
    },
    { deep: true },
)

const hasExtra = computed(() => props.extra != null)
const elementId = computed(() => betaStore.getElementId(props.element))
const isActive = computed(() => betaStore.isElementActive(elementId.value))
const backgroundImage = computed(() => props.extra?.background_image?.uri)
const backgroundImageOpacity = computed(() => props.extra?.background_image_opacity ?? '80')
const backgroundFocalPoint = computed(() => props.extra?.background_image_focal_point ?? { x: 50, y: 50 })

const dynamicStyle = computed(() => {
    if (!hasExtra.value) return undefined

    return {
        margin: `${props.extra.margin?.top ?? 0}px ${props.extra.margin?.right ?? 0}px ${props.extra.margin?.bottom ?? 0}px ${props.extra.margin?.left ?? 0}px`,
        padding: `${props.extra.padding?.top ?? 0}px ${props.extra.padding?.right ?? 0}px ${props.extra.padding?.bottom ?? 0}px ${props.extra.padding?.left ?? 0}px`,
        backgroundColor: props.extra.background_color,
        justifyContent: props.extra.vertical_align ?? 'center',
    }
})

const getSchema = (frameType: string, index: number) => {
    const schema = Array.isArray(props.schema) ? props.schema : []
    const lastLevel = schema[schema.length - 1]?.level ?? 0

    return [
        ...schema,
        {
            level: lastLevel + 1,
            element: frameType,
            index,
        },
    ]
}

const onEndDraggable = () => {
    betaStore.patchElementBySchema(props.schema, {
        frameContent: content.value,
    })
}
</script>

<template>
    <div
        tabindex="0"
        class="editor-container-beta"
        :data-active="isActive"
        :schema="JSON.stringify(schema)"
        :style="dynamicStyle"
    >
        <beta-base-image
            v-if="hasExtra && backgroundImage"
            :src="backgroundImage"
            class-name="absolute inset-0 z-0 h-full w-full object-cover"
            :style="{
                opacity: `${Number(backgroundImageOpacity) / 100}`,
                objectPosition: `${backgroundFocalPoint.x}% ${backgroundFocalPoint.y}%`,
            }"
        />

        <div class="relative z-10 flex min-w-0 max-w-full flex-wrap">
            <VueDraggable
                v-model="content"
                class="editor-elements-container-beta"
                :animation="150"
                handle=".beta-action-move-element"
                ghostClass="ghost"
                @end="onEndDraggable"
            >
                <template
                    v-for="(childElement, index) in content"
                    :key="childElement?.frameId || childElement?.temporaryId || index"
                >
                    <beta-editor-element
                        v-if="childElement"
                        :element="childElement"
                        :element-index="index"
                        :section-index="sectionIndex"
                        :section-background-color="sectionBackgroundColor"
                        :section-has-background-image="!!backgroundImage || sectionHasBackgroundImage"
                        :is-editable="isEditable"
                        :is-child="true"
                        :element-parent="props.element"
                        :element-parent-index="elementIndex"
                        :schema="getSchema(childElement.frameType, index)"
                        :heading-style="headingStyle"
                    />
                </template>
            </VueDraggable>
        </div>
    </div>
</template>

<style scoped>
.editor-container-beta {
    @apply relative flex min-w-0 max-w-full flex-col border border-dashed border-zinc-300 bg-transparent outline-none transition-all duration-1000 ease-in-out hover:border-[#D7E3F4] hover:shadow-[0_0_0_2px_rgba(215,227,244,0.22)];
    box-sizing: border-box;
}

.editor-container-beta[data-active='true'] {
    @apply border-[#8EB3E8] border-opacity-100 shadow-[0_0_0_3px_rgba(142,179,232,0.24)];
}

.editor-elements-container-beta {
    @apply relative z-10 flex w-full min-w-0 max-w-full flex-wrap justify-center;
    box-sizing: border-box;
    transition:
        min-height 1000ms ease-in-out,
        gap 1000ms ease-in-out;
}
</style>
